import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Package, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";

// Render vials sized to the number of items in the bundle (2 or 3 supported).
function BundleVials({ items }) {
  const n = items.length;
  return (
    <div className={`absolute inset-0 flex items-center justify-center px-6 ${n >= 3 ? "gap-2" : "gap-4"}`}>
      {items.map((label, i) => {
        const isCenter = n === 3 && i === 1;
        const rot = n === 3 ? (i === 0 ? -6 : i === 2 ? 6 : 0) : (i === 0 ? -4 : 4);
        const ty = n === 3 ? (isCenter ? 0 : i === 0 ? 10 : -10) : (i === 0 ? 10 : -10);
        const w = n === 3 ? "w-[26%]" : "w-[38%]";
        // Split label into name + strength ("BPC-157 10 mg" → ["BPC-157", "10 mg"])
        const parts = label.trim().split(/\s+/);
        const strength = parts.slice(-2).join(" ");
        const name = parts.slice(0, -2).join(" ") || label;
        return (
          <div
            key={i}
            className={`relative ${w} h-[76%] rounded-t-[16px] rounded-b-[8px] border border-[#2A2A2A] bg-[#0e0e0e] overflow-hidden`}
            style={{ transform: `translateY(${ty}px) rotate(${rot}deg)` }}
          >
            <div className="absolute top-0 left-0 right-0 h-[12%] bg-gradient-to-b from-[#2a2a2a] to-[#111] border-b border-[#2A2A2A]" />
            <div className="absolute top-[12%] left-0 right-0 h-[4%] bg-[#171717] border-b border-[#2A2A2A]" />
            <div className="absolute left-0 right-0 bottom-0 h-[60%] vial-liquid" />
            <div className="absolute left-1/2 -translate-x-1/2 top-[34%] w-[86%] px-2 py-2 bg-[#0B0B0B]/85 border border-[#2A2A2A] rounded-[6px]">
              <div className="label-mono text-[7.5px]">Peptide Labs N.I.</div>
              <div className="mt-1.5 text-white text-[9.5px] font-medium tracking-tight leading-tight line-clamp-1">{name}</div>
              <div className="mt-0.5 text-[#B7BCC7] text-[8.5px]">{strength}</div>
            </div>
            <div className="absolute left-0 right-0 bottom-[58%] h-[1.5px] bg-[#1565FF]/70 blur-[2px]" />
          </div>
        );
      })}
    </div>
  );
}

export const BundleCard = ({ bundle, testIdSuffix }) => {
  const { addItem } = useCart();
  const items = bundle.includes || [];
  const saved = (bundle.original_price || 0) - bundle.price;

  return (
    <div
      data-testid={`bundle-card-${testIdSuffix || bundle.slug}`}
      className="group relative flex flex-col rounded-3xl overflow-hidden border border-[#1565FF]/30 bg-[#0e0e0e] hover:border-[#1565FF]/60 transition-colors shadow-lab-glow"
    >
      {/* Vial visual */}
      <div className="relative aspect-[16/10] vial-shell overflow-hidden">
        <div className="absolute inset-0 grid-backdrop opacity-30" />
        <BundleVials items={items} />
        <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#1565FF] text-white">
          <Package size={11} />
          <span className="text-[10px] font-mono tracking-widest uppercase">Bundle</span>
        </div>
        {saved > 0 && (
          <div className="absolute top-4 right-4 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#0B0B0B]/80 border border-[#1565FF]/40 text-white">
            <span className="text-[10px] font-mono tracking-widest uppercase">Save £{saved.toFixed(0)}</span>
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="p-8 flex flex-col gap-5 border-t border-[#2A2A2A]">
        <div>
          <div className="label-mono">Starter Kit · {items.length}-vial</div>
          <h3 className="mt-3 text-2xl lg:text-3xl tracking-tight text-white font-medium">{bundle.name}</h3>
          <p className="mt-3 text-[#B7BCC7] text-[13.5px] leading-relaxed line-clamp-3">{bundle.short_description}</p>
        </div>

        <ul className="space-y-2">
          {items.map((line) => (
            <li key={line} className="flex items-center gap-3 text-white text-[13.5px]">
              <span className="w-5 h-5 rounded-full bg-[#1565FF]/15 border border-[#1565FF]/40 grid place-items-center text-[#1565FF]">
                <Plus size={10} />
              </span>
              {line}
            </li>
          ))}
        </ul>

        <div className="flex items-end justify-between border-t border-[#2A2A2A] pt-6">
          <div>
            <div className="label-mono">Bundle price</div>
            <div className="mt-2 flex items-baseline gap-2">
              {bundle.original_price && (
                <span className="text-[#6E7784] font-mono text-sm line-through">£{bundle.original_price.toFixed(2)}</span>
              )}
              <span className="text-white font-mono text-3xl tabular-nums">£{bundle.price.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => { addItem(bundle, 1); toast.success(`${bundle.name} added to cart`); }}
            data-testid={`bundle-add-to-cart-${bundle.slug}`}
            className="btn-primary inline-flex items-center gap-2 flex-1"
          >
            Add to Cart · £{bundle.price.toFixed(2)}
          </button>
          <Link
            to={`/products/${bundle.slug}`}
            data-testid={`bundle-details-${bundle.slug}`}
            className="btn-ghost inline-flex items-center gap-2"
          >
            Details <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
};
